import { useMemo } from "react"
import { dayjsUTC } from "../../utils/dates"

/**
 * Calculates start dates of each month within the range of data usage.
 * Also returns midpoints for labels.
 *
 * Aligns boundaries to the first Sunday of each month (week start).
 */
export const calculateMonthBoundaries = (dates: Date[]) => {
  if (dates.length === 0) return { startDates: [], monthCenters: [] }

  const start = dayjsUTC(dates[0])
  const end = dayjsUTC(dates[dates.length - 1])

  const startDates: number[] = []
  let current = start.startOf("month")

  // Generate boundaries for the first of every month until we pass the end date
  while (current.isBefore(end) || current.isSame(end, "day")) {
    // Only add the boundary if it is within the range, or add the start date if it's the first one
    if (current.valueOf() >= start.valueOf()) {
      startDates.push(current.valueOf())
    } else if (startDates.length === 0) {
      // If the month start is before data start, use data start as the boundary for the first bucket
      startDates.push(start.valueOf())
    }
    current = current.add(1, "month")
  }
  // Add one more boundary to close the last interval
  // If the last generated boundary is before the end, add the calculated next month start or end
  if (current.isAfter(end)) {
    startDates.push(current.valueOf())
  } else {
    // Should not normally happen with the while condition, but for safety
    startDates.push(current.add(1, "month").valueOf())
  }

  // Calculate centers for labels
  const monthCenters = startDates.slice(0, -1).map((curr, i) => {
    const next = startDates[i + 1] ?? end.valueOf()

    // Clamp the center calculation to the visible data range
    // This ensures labels appear centered in the visible portion of the month
    const visibleStart = Math.max(curr, start.valueOf())
    const visibleEnd = Math.min(next, end.valueOf())

    // Formatting: Use the month from the boundary (or start date)
    // Check if the full month is represented in the data
    const monthStart = dayjsUTC(curr).startOf("month")
    const monthEnd = monthStart.add(1, "month")
    const totalMonthDuration = monthEnd.valueOf() - monthStart.valueOf()
    const visibleDuration = visibleEnd - visibleStart

    // Formatting rules:
    // 1. If less than 2 weeks of data visible for this month, don't show label
    // 2. If partial month (but > 2 weeks), show short "MMM"
    // 3. If full month, show full "MMMM"
    const twoWeeksMs = 14 * 24 * 60 * 60 * 1000
    let label = ""

    if (visibleDuration >= twoWeeksMs) {
      const isPartial = visibleDuration < totalMonthDuration
      label = dayjsUTC(curr).format(isPartial ? "MMM" : "MMMM")
    }

    // If visible range is valid (start < end), center there.
    // If it's a single point or edge case (start >= end), use the start point.
    const x =
      visibleStart < visibleEnd ? (visibleStart + visibleEnd) / 2 : visibleStart

    return {
      x,
      label,
    }
  })

  return { startDates, monthCenters }
}

export const useMonthBoundaries = (dates: Date[]) => {
  return useMemo(() => calculateMonthBoundaries(dates), [dates])
}

/**
 * Wraps calculateMonthBoundaries to return indices instead of timestamps.
 * Useful for charts that use index-based x-axes (like Heatmaps with band scales).
 *
 * Scans the provided dates for month transitions to generate grid lines and labels.
 */
export const calculateMonthIndices = (dates: Date[]) => {
  const startIndices = new Set<number>()
  const monthCenters: { x: number; label: string }[] = []

  if (dates.length === 0) return { startIndices, monthCenters }

  // Scan for month changes
  let currentMonth = dayjsUTC(dates[0]).month()
  let currentMonthStartIdx = 0

  for (let i = 1; i < dates.length; i++) {
    const d = dayjsUTC(dates[i])
    if (d.month() !== currentMonth) {
      // Found a boundary between i-1 and i
      const boundaryIdx = i - 0.5
      startIndices.add(boundaryIdx)

      // Calculate center for the month just finished
      // Range is indices [currentMonthStartIdx, i - 1]
      // Visual range is [currentMonthStartIdx - 0.5, i - 0.5]
      // Center = ( (currentMonthStartIdx - 0.5) + (i - 0.5) ) / 2
      const label = dayjsUTC(dates[currentMonthStartIdx]).format("MMMM")
      monthCenters.push({
        x: (currentMonthStartIdx + i - 1) / 2,
        label,
      })

      currentMonth = d.month()
      currentMonthStartIdx = i
    }
  }

  // Add label for the final month
  // Visual range [currentMonthStartIdx - 0.5, dates.length - 0.5]
  const label = dayjsUTC(dates[currentMonthStartIdx]).format("MMMM")
  monthCenters.push({
    x: (currentMonthStartIdx + dates.length - 1) / 2,
    label,
  })

  return { startIndices, monthCenters }
}

export const useMonthIndices = (dates: Date[]) => {
  return useMemo(() => calculateMonthIndices(dates), [dates])
}
